'use client';

import { useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { Loader2 } from 'lucide-react';

import { consumePostLoginRedirect } from '@/lib/auth-redirect';

/**
 * 인증된 사용자가 루트 `/` 에 직접 도달했을 때, sessionStorage 의 post-login redirect
 * 를 소비해 원래 가려던 protected path 로 보낸다.
 *
 * 동작:
 *  - 마운트 시 consumePostLoginRedirect(): 있으면 그 path, 없으면 '/dashboard' 로 router.replace.
 *  - 짧은 client-side transition 동안 spinner 만 표시 (전체 화면 fixed 가 아닌 layout 안에 자연스럽게).
 *  - StrictMode 더블 마운트에서도 두 번 router.replace 가 호출되지 않도록 ref 가드.
 *
 * 보안:
 *  - consume 안에서 isSafeRedirectPath 검증 + open redirect 방지.
 *  - 잘못된 키 값은 자동으로 null 처리 → '/dashboard' fallback.
 */
export function PostLoginGate() {
  const router = useRouter();
  const dispatched = useRef(false);

  useEffect(() => {
    if (dispatched.current) return;
    dispatched.current = true;
    const target = consumePostLoginRedirect() ?? '/dashboard';
    router.replace(target);
  }, [router]);

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-primary" />
        <p className="text-sm text-muted-foreground">이동 중…</p>
      </div>
    </div>
  );
}
